import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import ConnectionBanner from '../components/ConnectionBanner';
import PhaseTrack from '../components/PhaseTrack';
import TvLot from '../components/TvLot';
import TvTeams from '../components/TvTeams';
import { CLOSE_CODES, ROLE_LABELS, STATES } from '../domain/constants';
import useAuctionSocket from '../hooks/useAuctionSocket';
import { fetchPlayers, indexPlayers, spectateAuction } from '../lib/api';
import {
    clearSpectatorSession,
    loadSpectatorSession,
    saveSpectatorSession,
} from '../lib/session';

// Lo schermo da proiettare: la stessa asta, in sola lettura e a caratteri
// grandi. Non manda mai un comando — il `send` del socket resta inutilizzato —
// e il server rifiuta comunque tutto tranne il ping.
//
// Il layout cambia con lo stato: mentre si rilancia il lotto prende la scena e
// le squadre restano in sintesi; appena il timer si ferma il lotto si ritira a
// una riga e le rose si aprono, che e' il momento in cui la gente le guarda.

export default function SpectatorPage() {
    const { lobbyName } = useParams();
    const [token, setToken] = useState(() => loadSpectatorSession(lobbyName)?.token ?? '');
    const [catalogue, setCatalogue] = useState({});
    const [catalogueError, setCatalogueError] = useState(null);

    const { auction, connection, remainingSeconds, staleProtocol } = useAuctionSocket(
        lobbyName,
        token,
    );

    useEffect(() => {
        let cancelled = false;
        fetchPlayers()
            .then((payload) => {
                if (!cancelled) setCatalogue(indexPlayers(payload.players));
            })
            .catch((error) => {
                if (!cancelled) setCatalogueError(error.message);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Un token non piu valido (lobby ricreata, snapshot perso) riporta al
    // modulo della password invece di lasciare lo schermo su un errore muto.
    useEffect(() => {
        if (connection.closedCode === CLOSE_CODES.BAD_TOKEN) {
            clearSpectatorSession(lobbyName);
            setToken('');
        }
    }, [connection.closedCode, lobbyName]);

    useScreenWakeLock(Boolean(token));

    const handleToken = useCallback(
        (fresh) => {
            saveSpectatorSession(lobbyName, { token: fresh });
            setToken(fresh);
        },
        [lobbyName],
    );

    if (!token) {
        return <SpectatorGate lobbyName={lobbyName} onToken={handleToken} />;
    }

    if (catalogueError) {
        return (
            <SpectatorGate
                lobbyName={lobbyName}
                onToken={handleToken}
                error={`Listone non disponibile: ${catalogueError}`}
            />
        );
    }

    if (!auction) {
        return (
            <div className="tv">
                <TvHeader lobbyName={lobbyName} />
                <ConnectionBanner connection={connection} staleProtocol={staleProtocol} />
                <p className="tv-status">Collegamento all&apos;asta…</p>
            </div>
        );
    }

    // Il timer scorre solo in questi due stati: e' l'unico momento in cui il
    // lotto vale piu' spazio delle rose.
    const live =
        auction.state === STATES.BIDDING || auction.state === STATES.AWAITING_CONFIRM;

    return (
        <div className={`tv${live ? ' tv--live' : ''}`}>
            <TvHeader
                lobbyName={auction.lobby_name}
                role={auction.current_phase_role}
                online={auction.online_ids?.length ?? 0}
                total={auction.participants.length}
            />
            <ConnectionBanner connection={connection} staleProtocol={staleProtocol} />
            <TvLot
                auction={auction}
                catalogue={catalogue}
                remainingSeconds={remainingSeconds}
                live={live}
            />
            <TvTeams
                auction={auction}
                catalogue={catalogue}
                detail={live ? 'summary' : 'roster'}
            />
        </div>
    );
}

function TvHeader({ lobbyName, role, online, total }) {
    return (
        <header className="tv-header">
            <h1 className="tv-title u-truncate">{lobbyName}</h1>
            {role && (
                <div className="tv-phase">
                    <PhaseTrack currentRole={role} />
                    <span className="tv-phase-label">{ROLE_LABELS[role]}</span>
                </div>
            )}
            {total > 0 && (
                <p className="tv-online num">
                    {online}/{total} <span className="u-caps">collegati</span>
                </p>
            )}
        </header>
    );
}

function SpectatorGate({ lobbyName, onToken, error }) {
    const [password, setPassword] = useState('');
    const [pending, setPending] = useState(false);
    const [failure, setFailure] = useState(null);

    const submit = async (event) => {
        event.preventDefault();
        setPending(true);
        setFailure(null);
        try {
            const response = await spectateAuction({
                lobbyName,
                password,
                token: loadSpectatorSession(lobbyName)?.token,
            });
            onToken(response.token);
        } catch (problem) {
            setFailure(problem.message);
            setPending(false);
        }
    };

    return (
        <div className="app">
            <main className="app-main">
                <div className="panel panel--centered">
                    <div className="panel-header">
                        <h2 className="panel-title">Modalità spettatore</h2>
                    </div>
                    <form className="panel-body stack" onSubmit={submit}>
                        <p className="u-dim">
                            Questa schermata mostra l&apos;asta <strong>{lobbyName}</strong> in
                            sola lettura: non occupa un posto e non puo&apos; fare offerte.
                        </p>
                        <div className="field">
                            <label className="field-label" htmlFor="tv-password">
                                Password della lobby
                            </label>
                            <input
                                id="tv-password"
                                type="password"
                                className="input"
                                value={password}
                                autoFocus
                                onChange={(event) => setPassword(event.target.value)}
                            />
                            {(failure || error) && (
                                <p className="field-hint field-hint--stop">{failure || error}</p>
                            )}
                        </div>
                        <div className="panel-actions">
                            <button type="submit" className="btn btn--primary" disabled={pending}>
                                {pending ? 'Attendere…' : 'Guarda l’asta'}
                            </button>
                            <Link className="btn btn--ghost" to="/asta-online">
                                Torna al portale
                            </Link>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
}

/** Tiene sveglio il computer del proiettore, dove il browser lo permette. */
function useScreenWakeLock(active) {
    useEffect(() => {
        if (!active || typeof navigator === 'undefined' || !navigator.wakeLock) {
            return undefined;
        }
        let sentinel = null;
        let released = false;

        const acquire = async () => {
            try {
                sentinel = await navigator.wakeLock.request('screen');
            } catch {
                // Batteria bassa, scheda in secondo piano, permesso negato:
                // niente da fare, e non e un errore da mostrare.
            }
        };
        // Il blocco cade da solo quando la scheda passa in secondo piano, e va
        // ripreso al ritorno: senza, basta un cambio di finestra per perderlo.
        const onVisible = () => {
            if (!released && document.visibilityState === 'visible') acquire();
        };

        acquire();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            released = true;
            document.removeEventListener('visibilitychange', onVisible);
            sentinel?.release?.().catch(() => {});
        };
    }, [active]);
}
